import React, { useEffect, useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Layout } from '../components/Layout';
import { QuoteWidget } from '../components/QuoteWidget';
import { WhyRefacing } from '../components/WhyRefacing';
import { MarqueeGallery } from '../components/MarqueeGallery';
import { TransformationSlider } from '../components/TransformationSlider';
import { ProcessGrid } from '../components/ProcessGrid';
import { Showroom } from '../components/Showroom';
import { ValueProp } from '../components/ValueProp';
import { FacebookIcon, InstagramIcon, TwitterIcon, PhoneIcon, MailIcon, MapPinIcon } from 'lucide-react';
const HERO_IMAGES = ['https://cdn.magicpatterns.com/uploads/uyAyGkhhVAJn3hZ2c4Gr3n/image.png', 'https://images.unsplash.com/photo-1588854337236-6889d631faa8?auto=format&fit=crop&q=80&w=800', 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&q=80&w=800', 'https://images.pexels.com/photos/1080721/pexels-photo-1080721.jpeg?auto=compress&cs=tinysrgb&w=800', 'https://images.pexels.com/photos/8186477/pexels-photo-8186477.jpeg?auto=compress&cs=tinysrgb&w=800'];
export function LandingPage() {
  const [currentImage, setCurrentImage] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImage(prev => (prev + 1) % HERO_IMAGES.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  return <Layout data-id="element-429">
      {/* Hero Section */}
      <header className="relative min-h-[75vh] flex flex-col lg:flex-row border-b-3 border-charcoal" data-id="element-430">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="flex-1 bg-alabaster p-8 md:p-12 flex flex-col justify-center border-r-0 lg:border-r-3 border-charcoal relative overflow-hidden" data-id="element-431">
          {/* Mobile Background Carousel */}
          <div className="absolute inset-0 lg:hidden pointer-events-none" data-id="element-432">
            <AnimatePresence mode="wait" data-id="element-433">
              <motion.img key={currentImage} src={HERO_IMAGES[currentImage]} initial={{
              opacity: 0
            }} animate={{
              opacity: 0.8
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 1.5
            }} className="w-full h-full object-cover grayscale contrast-75" data-id="element-434" />
            </AnimatePresence>
            {/* 40% Dark Overlay */}
            <div className="absolute inset-0 bg-black/80" data-id="element-435" />
          </div>

          <div className="max-w-xl relative z-10" data-id="element-436">
            <motion.span variants={itemVariants} className="inline-block bg-espresso/10 text-espresso lg:bg-espresso/10 lg:text-espresso px-4 py-2 font-bold uppercase tracking-widest text-xs mb-4 bg-white/10 text-alabaster" data-id="element-437">
              Premium Cabinet Refacing
            </motion.span>
            <motion.h1 variants={itemVariants} className="text-4xl md:text-6xl font-bold leading-[0.95] mb-4 text-alabaster lg:text-charcoal" data-id="element-438">
              A Stunning <br data-id="element-439" />
              <span className="italic font-serif font-normal text-espresso" data-id="element-440">
                Kitchen
              </span>{' '}
              <br data-id="element-441" />
              Transformation <br data-id="element-442" />
              At 50% Less Cost.
            </motion.h1>
            <motion.p variants={itemVariants} className="text-lg mb-6 leading-relaxed text-alabaster/80 lg:text-charcoal/60" data-id="element-443">
              Achieve the look of a $30,000 custom kitchen remodel in just 48
              hours. Our precision refacing technology gives you brand-new doors
              and finishes without the demolition mess.
            </motion.p>
            <motion.div variants={itemVariants} className="flex flex-wrap gap-8 items-center" data-id="element-444">
              <div className="flex -space-x-4" data-id="element-445">
                {[1, 2, 3, 4].map(i => <div key={i} className="w-12 h-12 rounded-full border-4 border-alabaster bg-charcoal/10 overflow-hidden" data-id="element-446">
                    <img src={`https://i.pravatar.cc/150?u=${i}`} alt="User" data-id="element-447" />
                  </div>)}
              </div>
              <p className="text-sm font-bold text-alabaster lg:text-charcoal" data-id="element-448">
                <span className="text-espresso" data-id="element-449">500+</span> Kitchens Refaced in
                MD/VA
              </p>
            </motion.div>
          </div>
        </motion.div>

        <div className="flex-1 bg-charcoal/5 p-6 md:p-12 flex items-center justify-center relative overflow-hidden" data-id="element-450">
          {/* Background Image for Quote Area */}
          <div className="absolute inset-0 z-0" data-id="element-451">
            <img src="https://images.pexels.com/photos/8186477/pexels-photo-8186477.jpeg?auto=compress&cs=tinysrgb&w=1200" alt="Luxury Kitchen Background" className="w-full h-full object-cover opacity-20" data-id="element-452" />
            <div className="absolute inset-0 bg-gradient-to-br from-charcoal/40 to-transparent" data-id="element-453" />
          </div>

          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-5 pointer-events-none z-0" style={{
          backgroundImage: 'radial-gradient(#1A1A1A 1px, transparent 1px)',
          backgroundSize: '24px 24px'
        }} data-id="element-454" />

          <div className="w-full max-w-md relative z-10" data-id="element-455">
            <QuoteWidget data-id="element-456" />
          </div>
        </div>
      </header>

      <MarqueeGallery data-id="element-457" />
      <WhyRefacing data-id="element-458" />
      <TransformationSlider data-id="element-459" />
      <ProcessGrid data-id="element-460" />
      <Showroom data-id="element-461" />
      <ValueProp data-id="element-462" />
    </Layout>;
}